import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import PilotProfileReview from "@/components/athlete/PilotProfileReview";
import { hasDirectSupport, type ProfileAthlete } from "@/lib/profileModules";
import {
  attentionTasks, completenessItems, CONSENT_VERSION, percent, readinessGates, STAGE_LABEL, STAGES,
  type HubState, type Stage,
} from "@/lib/athleteHub";

type Inquiry = { id: string; company: string; opportunity_type: string; estimated_budget: string | null; timing: string | null; description: string | null; status: string; created_at: string };
export type Hub = HubState & { follower_count: number; inquiries: Inquiry[]; changes_requested: string | null };

const err = (e: any) => e?.message ?? "Something went wrong";

/** Athlete Dashboard Home: what needs attention, completeness, readiness, followers, opportunities, Back Her Game. */
const AthleteHubHome = ({ athlete, causeCount, onGo, fixture }: {
  athlete: ProfileAthlete & { id: string; slug: string; published: boolean };
  causeCount: number;
  onGo: (where: "Profile" | "Causes" | "Support") => void;
  /** Admin visual QA only: render this in-memory state, read nothing, write nothing. */
  fixture?: { hub: Hub; shopStatus: string | null };
}) => {
  const [hub, setHub] = useState<Hub | null>(fixture?.hub ?? null);
  const [shopStatus, setShopStatus] = useState<string | null>(fixture?.shopStatus ?? null);
  // Fixture mode keeps every write action disabled.
  const [busy, setBusy] = useState(Boolean(fixture));
  const [note, setNote] = useState("");
  const [consentPub, setConsentPub] = useState(false);
  const [consentLike, setConsentLike] = useState(false);

  const load = async () => {
    if (fixture) return;
    const [{ data, error }, cs] = await Promise.all([
      supabase.rpc("athlete_my_hub"),
      supabase.from("athlete_commerce_settings").select("shop_status").eq("athlete_id", athlete.id).maybeSingle(),
    ]);
    if (error) return toast.error(err(error));
    setHub(data as unknown as Hub);
    setShopStatus((cs.data as any)?.shop_status ?? null);
  };
  useEffect(() => {
    if (fixture) { setHub(fixture.hub); setShopStatus(fixture.shopStatus); return; }
    load();
  }, [athlete.id, fixture]);

  if (!hub) return <p className="text-sm text-muted-foreground">Loading your hub…</p>;

  const content = completenessItems(athlete, hub.module_choices ?? {}, causeCount);
  const pct = percent(content);
  const gates = readinessGates({ ...hub, published: athlete.published });
  const tasks = attentionTasks({ ...hub, published: athlete.published }, content);
  const direct = hasDirectSupport(athlete).active;

  const approve = async (ok: boolean) => {
    setBusy(true);
    try {
      let hash: string | null = null;
      if (ok) {
        const h = await supabase.rpc("athlete_my_profile_hash");
        if (h.error) throw h.error;
        hash = h.data as string;
      }
      const { data, error } = await supabase.rpc("athlete_review_profile", { _approve: ok, _expected_hash: hash ?? "", _note: note });
      if (error) throw error;
      setHub(data as unknown as Hub);
      toast.success(ok ? "Thanks — you approved this version of your profile." : "Sent to SIIS. We'll update your profile.");
      setNote("");
    } catch (e) { toast.error(err(e)); } finally { setBusy(false); }
  };

  const saveConsents = async () => {
    setBusy(true);
    try {
      for (const [kind, on, have] of [["profile_publication", consentPub, hub.consents.profile_publication], ["photo_likeness", consentLike, hub.consents.photo_likeness]] as const) {
        if (on && !have) {
          const { error } = await supabase.rpc("athlete_record_consent", { _kind: kind, _granted: true, _version: CONSENT_VERSION });
          if (error) throw error;
        }
      }
      toast.success("Consent saved");
      await load();
    } catch (e) { toast.error(err(e)); } finally { setBusy(false); }
  };

  const withdraw = async (kind: "profile_publication" | "photo_likeness") => {
    if (!window.confirm("Withdraw this consent? Your public profile will be hidden immediately until you give consent again.")) return;
    setBusy(true);
    try {
      const { error } = await supabase.rpc("athlete_record_consent", { _kind: kind, _granted: false, _version: CONSENT_VERSION });
      if (error) throw error;
      toast.success("Consent withdrawn. SIIS has been notified.");
      if (kind === "profile_publication") setConsentPub(false); else setConsentLike(false);
      await load();
    } catch (e) { toast.error(err(e)); } finally { setBusy(false); }
  };

  const go = (where?: string) => {
    if (where === "profile") onGo("Profile");
    else if (where === "causes") onGo("Causes");
    else if (where === "support") onGo("Support");
  };
  const card = "rounded-2xl border border-border bg-card p-5 shadow-card";
  const athleteTasks = tasks.filter((t) => t.kind === "athlete");
  const waiting = tasks.filter((t) => t.kind === "waiting");
  const stageIdx = STAGES.indexOf(hub.stage as Stage);
  const gatesDone = gates.filter((g) => g.done).length;
  const first = athlete.name.split(" ")[0];
  const unread = hub.inquiries.filter((i) => i.status === "new").length;
  const taskAction = (t: (typeof tasks)[number]) => {
    const cls = "inline-flex min-h-[44px] shrink-0 items-center rounded-full bg-primary-strong px-4 text-xs font-semibold text-primary-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
    if (t.where === "quick_start") return <Link to="/dashboard/quick-start" className={cls}>Start</Link>;
    if (t.where === "approve") return <a href="#approve-h" className={cls}>Review</a>;
    if (t.where === "consent") return <a href="#consent-h" className={cls}>Confirm</a>;
    if (["profile", "causes", "support"].includes(t.where ?? "")) return <button type="button" className={cls} onClick={() => go(t.where)}>Add</button>;
    return null;
  };

  return (
    <div className="space-y-6">
      <header>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary-strong">Your business hub</p>
        <h2 className="font-display text-3xl uppercase leading-none mt-1">Hi, {first}</h2>
      </header>

      {/* WHERE I STAND */}
      <section aria-labelledby="stand-h" className="space-y-3">
        <h2 id="stand-h" className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Where I stand</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className={card}>
            <p className="text-sm text-muted-foreground">Profile completeness</p>
            <p className="font-display text-4xl leading-none mt-1">{pct}%</p>
            <div className="mt-3 h-2 overflow-hidden rounded-full bg-muted" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label="Profile completeness">
              <div className="h-full bg-primary-strong transition-all" style={{ width: `${pct}%` }} />
            </div>
            <p className="mt-2 text-xs text-muted-foreground">Only counts the sections you chose.</p>
          </div>
          <div className={card}>
            <p className="text-sm text-muted-foreground">Launch stage</p>
            <p className="font-display text-2xl leading-tight mt-1">{STAGE_LABEL[hub.stage as Stage]}</p>
            <ol className="mt-3 flex gap-1" aria-label={`Stage ${stageIdx + 1} of ${STAGES.length}`}>
              {STAGES.map((s, i) => (
                <li key={s} title={STAGE_LABEL[s]} className={`h-2 flex-1 rounded-full ${i <= stageIdx ? "bg-primary-strong" : "bg-muted"}`}>
                  <span className="sr-only">{STAGE_LABEL[s]}{i <= stageIdx ? " — reached" : ""}</span>
                </li>
              ))}
            </ol>
            <p className="mt-2 text-xs text-muted-foreground">{gatesDone} of {gates.length} launch steps done</p>
          </div>
        </div>
      </section>

      {/* WHAT NEEDS MY ATTENTION */}
      <section aria-labelledby="attn-h" className="space-y-3">
        <h2 id="attn-h" className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">What needs my attention</h2>
        <div className={card}>
          {athleteTasks.length === 0 ? (
            <p className="text-sm">You're all caught up. Nice work. 🙌</p>
          ) : (
            <ul className="divide-y divide-border">
              {athleteTasks.slice(0, 6).map((t) => (
                <li key={t.key} className="flex items-center justify-between gap-3 py-2.5 text-sm first:pt-0 last:pb-0">
                  <span className="min-w-0 break-words">{t.label}</span>
                  {taskAction(t)}
                </li>
              ))}
            </ul>
          )}
          {athleteTasks.length > 6 && <p className="mt-2 text-xs text-muted-foreground">+{athleteTasks.length - 6} more in your checklist below</p>}
          {waiting.map((w) => <p key={w.key} className="mt-3 rounded-lg bg-muted px-3 py-2 text-sm text-muted-foreground">⏳ {w.label} — nothing needed from you.</p>)}
          {hub.changes_requested && hub.stage === "siis_review" && <p className="mt-2 text-xs text-muted-foreground">Your note to SIIS: “{hub.changes_requested}”</p>}
        </div>

      {!fixture && <PilotProfileReview athleteId={athlete.id} slug={athlete.slug} published={athlete.published} approvedCurrent={hub.approved_current_version} onChanged={load} />}

      {hub.stage === "athlete_approval" && !hub.approved_current_version && (
        <section className={card} aria-labelledby="approve-h">
          <h2 id="approve-h" className="font-semibold">Approve your profile</h2>
          <p className="mt-1 text-sm text-muted-foreground">Look over your profile exactly as fans will see it, then approve it or tell SIIS what to change.</p>
          <a href={`/athlete/${athlete.slug}`} target="_blank" rel="noreferrer" className="mt-3 inline-block text-sm font-semibold text-primary-strong">Preview my profile ↗</a>
          <textarea aria-label="What should change?" value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} placeholder="What should change? (only needed if requesting changes)" className="mt-3 w-full rounded-lg border border-input bg-background p-2 text-sm" rows={3} />
          <div className="mt-3 flex flex-wrap gap-2">
            <Button disabled={busy} onClick={() => approve(true)}>Approve this version</Button>
            <Button variant="outline" disabled={busy || !note.trim()} onClick={() => approve(false)}>Request changes</Button>
          </div>
        </section>
      )}

      {(!hub.consents.profile_publication || !hub.consents.photo_likeness) && (
        <section className={card}>
          <h2 id="consent-h" className="font-semibold">Your consent</h2>
          <div className="mt-3 space-y-3 text-sm">
            {!hub.consents.profile_publication && (
              <label className="flex items-start gap-2"><Checkbox checked={consentPub} onCheckedChange={(v) => setConsentPub(!!v)} className="mt-0.5" />
                <span>I agree that Say It In Sports may publish my athlete profile once I've approved it.</span></label>
            )}
            {!hub.consents.photo_likeness && (
              <label className="flex items-start gap-2"><Checkbox checked={consentLike} onCheckedChange={(v) => setConsentLike(!!v)} className="mt-0.5" />
                <span>I agree that Say It In Sports may use my name, photos and likeness on my profile and to promote it.</span></label>
            )}
            <Button size="sm" disabled={busy || (!consentPub && !consentLike)} onClick={saveConsents}>Save consent</Button>
          </div>
        </section>
      )}
      </section>

      {/* AUDIENCE · OPPORTUNITIES · BACK HER GAME */}
      <div className="grid gap-3 sm:grid-cols-2">
        <section className={card} aria-labelledby="aud-h">
          <h2 id="aud-h" className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">My audience</h2>
          <p className="mt-2 font-display text-4xl leading-none">{hub.follower_count}</p>
          <p className="mt-1 text-sm text-muted-foreground">{hub.follower_count === 1 ? "fan follows you" : "fans follow you"} in My Corner</p>
        </section>
        <section className={card} aria-labelledby="bhg-h">
          <h2 id="bhg-h" className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Back Her Game</h2>
          <dl className="mt-3 space-y-2 text-sm">
            <div className="flex items-center justify-between gap-2"><dt>Direct Support</dt>
              <dd className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${direct ? "bg-primary/15 text-primary-strong" : "bg-muted text-muted-foreground"}`}>{direct ? "On" : "Off"}</dd></div>
            <div className="flex items-center justify-between gap-2"><dt>Her Collection</dt>
              <dd className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${shopStatus === "live" ? "bg-primary/15 text-primary-strong" : "bg-muted text-muted-foreground"}`}>
                {shopStatus === "live" ? "Live" : hub.module_choices?.her_collection === "interested" ? "Interested" : "Not active"}</dd></div>
          </dl>
          {hub.module_choices?.her_collection === "interested" && shopStatus !== "live" && (
            <p className="mt-2 text-xs text-muted-foreground">SIIS has your interest on file. Nothing goes on sale until it's approved together.</p>
          )}
        </section>
      </div>

      <section className={card} aria-labelledby="opp-h">
        <div className="flex items-baseline justify-between gap-2">
          <h2 id="opp-h" className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">My opportunities</h2>
          {unread > 0 && <span className="rounded-full bg-primary-strong px-2.5 py-0.5 text-xs font-semibold text-primary-foreground">{unread} new</span>}
        </div>
        {hub.inquiries.length === 0 ? <p className="mt-2 text-sm text-muted-foreground">No partnership inquiries yet. When a brand reaches out, it shows up here.</p> : (
          <ul className="mt-3 divide-y divide-border">
            {hub.inquiries.slice(0, 5).map((i) => (
              <li key={i.id} className="py-3 text-sm">
                <div className="flex justify-between gap-2"><strong className="break-words">{i.company}</strong><span className="shrink-0 text-xs text-muted-foreground">{new Date(i.created_at).toLocaleDateString()}</span></div>
                <p className="text-muted-foreground">{[i.opportunity_type, i.estimated_budget, i.timing].filter(Boolean).join(" · ")}</p>
                {i.description && <p className="mt-1 line-clamp-2">{i.description}</p>}
              </li>
            ))}
          </ul>
        )}
        <p className="mt-2 text-xs text-muted-foreground">Read-only. SIIS handles contact with each company for you.</p>
      </section>

      {/* SECONDARY */}
      <section aria-labelledby="tools-h" className="space-y-3">
        <h2 id="tools-h" className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Edit & settings</h2>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => onGo("Profile")}>Edit profile</Button>
          <Button variant="outline" size="sm" onClick={() => onGo("Causes")}>Causes</Button>
          <Button variant="outline" size="sm" onClick={() => onGo("Support")}>Direct Support</Button>
          <Button variant="outline" size="sm" asChild><a href={`/athlete/${athlete.slug}`} target="_blank" rel="noreferrer">View my profile ↗</a></Button>
        </div>
        <details className={card}>
          <summary className="cursor-pointer min-h-[44px] flex items-center font-semibold">Full checklist</summary>
          <h3 className="mt-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Profile content</h3>
          <ul className="mt-1 space-y-1 text-sm">{content.map((c) => <li key={c.key}>{c.done ? "✓" : "○"} {c.label}</li>)}</ul>
          <h3 className="mt-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Launch steps</h3>
          <ul className="mt-1 space-y-1 text-sm">{gates.map((g) => <li key={g.key}>{g.done ? "✓" : "○"} {g.label}{g.owner === "siis" && !g.done ? " (SIIS)" : ""}</li>)}</ul>
        </details>
        {(hub.consents.profile_publication || hub.consents.photo_likeness) && (
          <details className={card}>
            <summary className="cursor-pointer min-h-[44px] flex items-center font-semibold">Consent you've given</summary>
            <ul className="mt-2 space-y-2 text-sm">
              {([["profile_publication", "Publishing my profile"], ["photo_likeness", "Use of my name, photos and likeness"]] as const)
                .filter(([k]) => hub.consents[k]).map(([k, label]) => (
                <li key={k} className="flex items-center justify-between gap-3">
                  <span>✓ {label}</span>
                  <button className="min-h-[44px] px-2 text-xs font-semibold text-destructive" disabled={busy} onClick={() => withdraw(k)}>Withdraw</button>
                </li>
              ))}
            </ul>
            <p className="mt-2 text-xs text-muted-foreground">Withdrawing hides your public profile right away until you give consent again.</p>
          </details>
        )}
      </section>
    </div>
  );
};

export default AthleteHubHome;
